/** A parameterless function callback. */
type Action = () => void;

/**
 * Creates an action scheduler function that will execute on a future animation frame.
 *
 * Note: In my experience spinning on frames is a more consistent approximation of CPU-bound
 * work than setTimeout. When the CPU is busy, animation slows down.
 * @param action A parameterless function to execute on future animation frame.
 */
export const animationFrameDelay = (action: Action) => {
  // Used to debounce animation frames (this is for idempotent work so there shouldn't be a case
  // where the specific action is executed multiple times on the same frame tick).
  let animationFrameId: number;

  /**
   * Schedule the action to run on the next animation frame.
   * @param waitFrames Optional: pass the number of elapsed frames before execution (default is next frame).
   */
  const frameSpin = (waitFrames: number = 0) => {
    // Debounce: if this function is scheduled multiple times synchronously (before
    // the queued animation frame callback runs), cancel the prior scheduled execution and rerun.
    window.cancelAnimationFrame(animationFrameId);

    animationFrameId = window.requestAnimationFrame(() => {
      // Base case: waitFrames is zero => invoke action on this immediate frame.
      if (waitFrames === 0) {
        action();
        return;
      }

      // Recursively kick the action to the next animation frame to handle with decremented waitFrames.
      frameSpin(waitFrames - 1);
    });
  };
  return frameSpin;
};
